import { devtools, persist } from 'zustand/middleware'
import { shallow } from 'zustand/shallow'
import { createWithEqualityFn } from 'zustand/traditional'

export const AUTH_TENANT_STORE_KEY = '@bitlyft/auth-tenant'
const LEGACY_APP_STORE_KEY = '@bitlyft/app'

type TenantState = {
	currentTenant: string | undefined
}

type TenantActions = {
	setCurrentTenant: (id: string | undefined) => void
	reset: () => void
}

type TenantStore = TenantState & {
	actions: TenantActions
}

/**
 * Migrates tenant stickiness from the old app-shell store.
 *
 * Malformed localStorage is ignored so a bad legacy payload cannot block
 * sign-in; the session seed will repopulate the tenant from `/me`.
 */
const readLegacyCurrentTenant = (): string | undefined => {
	if (typeof localStorage === 'undefined') return undefined

	try {
		const legacyStore = JSON.parse(
			localStorage.getItem(LEGACY_APP_STORE_KEY) ?? '{}',
		) as { state?: { currentTenant?: unknown } }

		return typeof legacyStore.state?.currentTenant === 'string'
			? legacyStore.state.currentTenant
			: undefined
	} catch {
		return undefined
	}
}

const initialState: TenantState = {
	currentTenant: readLegacyCurrentTenant(),
}

const emptyState: TenantState = {
	currentTenant: undefined,
}

/**
 * Owns tenant selection for auth/session code.
 *
 * Keep this in shared/auth so routes, app-shell UI, and auth guards can read
 * the active tenant without shared modules importing feature state.
 */
const authTenantStore = createWithEqualityFn<TenantStore>()(
	devtools(
		persist(
			(set) => ({
				...initialState,
				actions: {
					setCurrentTenant: (id) =>
						set({ currentTenant: id }, false, 'authTenant/setCurrentTenant'),
					reset: () => set(emptyState, false, 'authTenant/reset'),
				},
			}),
			{
				name: AUTH_TENANT_STORE_KEY,
				partialize: ({ currentTenant }) => ({ currentTenant }),
			},
		),
		{ name: AUTH_TENANT_STORE_KEY },
	),
	shallow,
)

/**
 * React selector for the active tenant.
 */
export const useCurrentTenantValue = () =>
	authTenantStore((state) => state.currentTenant)

/**
 * Reads the active tenant outside React for route guards and loaders.
 */
export const getCurrentTenant = () => authTenantStore.getState().currentTenant

/**
 * Writes tenant selection from the dashboard session seed.
 */
export const setCurrentTenant = (id: string | undefined) =>
	authTenantStore.getState().actions.setCurrentTenant(id)

/**
 * Clears tenant selection in tests and sign-out cleanup.
 */
export const resetCurrentTenant = () =>
	authTenantStore.getState().actions.reset()
